import { Button, Icons, Logo, useTts } from '@slingu/ui';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { Cefr } from './curriculum/types.js';

import './quiz.css';

type Question = {
  id: string;
  cefr: Cefr;
  prompt: string;
  choices: string[];
  answerIndex: number;
  hint: string;
  audioText?: string; // se presente → pergunta sonora (sem mostrar o prompt antes)
};

const QUESTIONS: Question[] = [
  { id: 'q1', cefr: 'A1', prompt: 'Como se diz "Olá" em inglês?', choices: ['Hello', 'Goodbye', 'Please', 'Sorry'], answerIndex: 0, hint: 'A1 · vocabulário' },
  {
    id: 'q2',
    cefr: 'A1',
    prompt: 'Qual frase você ouviu?',
    choices: ['Good morning, everyone!', 'Good night, everyone!', 'Goodbye, everyone!', 'Hello, everyone!'],
    answerIndex: 0,
    hint: 'A1 · compreensão oral',
    audioText: 'Good morning, everyone!',
  },
  { id: 'q3', cefr: 'A2', prompt: '"Yesterday I ___ a movie." (watch)', choices: ['watched', 'watch', 'watches', 'watching'], answerIndex: 0, hint: 'A2 · passado simples' },
  {
    id: 'q4',
    cefr: 'A2',
    prompt: 'Qual frase você ouviu?',
    choices: [
      'She walked to the park every morning.',
      'She walks to the park every morning.',
      'She walking to the park every morning.',
      'She walk to the park every morning.',
    ],
    answerIndex: 0,
    hint: 'A2 · listening + tempo verbal',
    audioText: 'She walked to the park every morning.',
  },
  { id: 'q5', cefr: 'B1', prompt: '"If it rains, I ___ stay home."', choices: ['will', 'would', 'am', 'did'], answerIndex: 0, hint: 'B1 · condicionais' },
  {
    id: 'q6',
    cefr: 'B1',
    prompt: 'Qual pergunta você ouviu?',
    choices: [
      'How long have you lived in Paris?',
      'How long do you live in Paris?',
      'How long will you live in Paris?',
      'How long did you live in Paris?',
    ],
    answerIndex: 0,
    hint: 'B1 · present perfect auditivo',
    audioText: 'How long have you lived in Paris?',
  },
  { id: 'q7', cefr: 'B2', prompt: 'Voz passiva: "The book ___ by millions."', choices: ['was read', 'read', 'is reading', 'reads'], answerIndex: 0, hint: 'B2 · passive voice' },
  { id: 'q8', cefr: 'B2', prompt: '"If I ___ known, I would have told you."', choices: ['had', 'have', 'did', 'was'], answerIndex: 0, hint: 'B2 · condicional 3' },
  { id: 'q9', cefr: 'C1', prompt: 'Idiom: "A piece of cake" significa…', choices: ['Muito fácil', 'Muito gostoso', 'Muito complicado', 'Muito chato'], answerIndex: 0, hint: 'C1 · idioms' },
  { id: 'q10', cefr: 'C1', prompt: 'Softer: "You are wrong." → "You ___ be mistaken."', choices: ['might', 'will', 'must', 'are'], answerIndex: 0, hint: 'C1 · hedging' },
];

const BANDS: Cefr[] = ['A1', 'A2', 'B1', 'B2', 'C1'];

export type PlacementOutcome = {
  level: Cefr;
  scorePerBand: Record<Cefr, { correct: number; total: number }>;
  suggestedSectionId: string;
  suggestedUnitId: string;
};

const EMPTY: Record<Cefr, { correct: number; total: number }> = {
  A1: { correct: 0, total: 0 },
  A2: { correct: 0, total: 0 },
  B1: { correct: 0, total: 0 },
  B2: { correct: 0, total: 0 },
  C1: { correct: 0, total: 0 },
};

const DEST: Record<Cefr, { section: string; unit: string }> = {
  A1: { section: 's1', unit: 'u1' },
  A2: { section: 's2', unit: 'u6' },
  B1: { section: 's3', unit: 'u11' },
  B2: { section: 's4', unit: 'u16' },
  C1: { section: 's4', unit: 'u20' },
};

export function PlacementQuiz({ onDone }: { onDone: (o: PlacementOutcome) => void }) {
  const [started, setStarted] = useState(false);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Record<string, boolean>>({});

  const current = QUESTIONS[idx];
  const progress = started ? Math.round((idx / QUESTIONS.length) * 100) : 0;

  function submit() {
    if (selected === null || !current) return;
    const ok = selected === current.answerIndex;
    const nextAnswers = { ...answers, [current.id]: ok };
    setAnswers(nextAnswers);
    setSelected(null);
    if (idx + 1 >= QUESTIONS.length) finalize(nextAnswers);
    else setIdx(idx + 1);
  }

  function finalize(a: Record<string, boolean>) {
    const scorePerBand: Record<Cefr, { correct: number; total: number }> = {
      A1: { correct: 0, total: 0 },
      A2: { correct: 0, total: 0 },
      B1: { correct: 0, total: 0 },
      B2: { correct: 0, total: 0 },
      C1: { correct: 0, total: 0 },
    };
    for (const q of QUESTIONS) {
      scorePerBand[q.cefr].total += 1;
      if (a[q.id]) scorePerBand[q.cefr].correct += 1;
    }
    let best: Cefr = 'A1';
    for (const b of BANDS) {
      const band = scorePerBand[b];
      if (band.total && band.correct / band.total >= 0.7) best = b;
    }
    const dest = DEST[best];
    onDone({ level: best, scorePerBand, suggestedSectionId: dest.section, suggestedUnitId: dest.unit });
  }

  function skipToStart() {
    onDone({
      level: 'A1',
      scorePerBand: EMPTY,
      suggestedSectionId: 's-greet',
      suggestedUnitId: 'u1',
    });
  }

  return (
    <div className="sl-quiz">
      <header className="sl-quiz__topbar">
        <span aria-hidden />
        <span className="sl-quiz__brand">
          <Logo size={28} /> <span>Slingu</span>
        </span>
        <button
          type="button"
          className="sl-quiz__skip"
          onClick={skipToStart}
          aria-label="Pular teste e começar do zero"
        >
          Pular
        </button>
      </header>

      <main className="sl-quiz__main">
        {!started ? (
          <section className="sl-quiz__card">
            <span className="sl-quiz__eyebrow">
              <Icons.SparkleIcon size={14} />
              Teste de nivelamento
            </span>
            <h1 className="sl-quiz__prompt">Vamos descobrir seu nível</h1>
            <p className="sl-quiz__intro-body">
              São 10 perguntas rápidas (A1 → C1). Com isso a gente abre a unidade
              certa pra você — nada de repetir "hello" se você já sabe idioms.
            </p>
            <p className="sl-quiz__intro-body" style={{ color: 'var(--sl-accent)' }}>
              Sem pressão: dá pra pular e começar por A1 do zero.
            </p>
          </section>
        ) : current ? (
          <QuizCard
            key={current.id}
            question={current}
            idx={idx}
            total={QUESTIONS.length}
            progress={progress}
            selected={selected}
            onSelect={setSelected}
          />
        ) : null}
      </main>

      <footer className="sl-quiz__footer">
        {!started ? (
          <div className="sl-quiz__actions">
            <Button size="lg" onClick={() => setStarted(true)}>
              Começar teste
            </Button>
            <Button variant="secondary" size="lg" onClick={skipToStart}>
              Começar do zero
            </Button>
          </div>
        ) : (
          <div className="sl-quiz__actions">
            <Button size="lg" onClick={submit} disabled={selected === null}>
              {idx + 1 >= QUESTIONS.length ? 'Finalizar teste' : 'Próxima'}
            </Button>
          </div>
        )}
      </footer>
    </div>
  );
}

function QuizCard({
  question,
  idx,
  total,
  progress,
  selected,
  onSelect,
}: {
  question: Question;
  idx: number;
  total: number;
  progress: number;
  selected: number | null;
  onSelect: (i: number) => void;
}) {
  const { play, stop, state } = useTts();
  const isAudio = !!question.audioText;
  const playedRef = useRef<string | null>(null);

  // Auto-play once per question. The ref guards against React 18 strict-mode
  // double-effect and parent re-renders that would otherwise trigger a
  // second overlapping playback (audio duplicado).
  useEffect(() => {
    if (!question.audioText) return;
    if (playedRef.current === question.id) return;
    playedRef.current = question.id;
    void play(question.audioText);
    return () => {
      stop();
    };
  }, [question.id, question.audioText, play, stop]);

  return (
    <section className="sl-quiz__card">
      <div className="sl-quiz__progress-wrap">
        <div
          className="sl-progress"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Progresso"
        >
          <div className="sl-progress__fill" style={{ width: `${progress}%` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <span className="sl-quiz__hint">
            Pergunta {idx + 1} de {total}
          </span>
          <span className="sl-quiz__hint" style={{ color: 'var(--sl-new-word)' }}>
            {question.hint}
          </span>
        </div>
      </div>

      {isAudio ? (
        <div className="sl-quiz__audio">
          <button
            type="button"
            className={`sl-quiz__audio-btn${state === 'playing' ? ' sl-quiz__audio-btn--playing' : ''}`}
            onClick={() => question.audioText && play(question.audioText)}
            aria-label="Ouvir novamente"
          >
            <Icons.HeadphonesIcon size={36} />
          </button>
          <p className="sl-quiz__audio-hint">
            {state === 'playing' ? 'Ouvindo…' : 'Toque para ouvir novamente'}
          </p>
        </div>
      ) : null}

      <h2 className="sl-quiz__prompt">{question.prompt}</h2>

      <ul className="sl-quiz__choices">
        {question.choices.map((c, i) => (
          <li key={c}>
            <button
              type="button"
              className={`sl-choice${selected === i ? ' sl-choice--selected' : ''}`}
              onClick={() => onSelect(i)}
            >
              <span className="sl-choice__label">{c}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

const BAND_COPY: Record<Cefr, { title: string; body: string; section: string }> = {
  A1: { title: 'Iniciante', body: 'Tudo começa com cumprimentos, nomes e verbo "to be". Vamos do zero juntos.', section: 'Novice' },
  A2: { title: 'Básico', body: 'Você conhece o essencial — bora refinar passado simples, perguntas e viagens.', section: 'Explorer' },
  B1: { title: 'Intermediário', body: 'Conversas reais: futuro, condicionais, present perfect e modais.', section: 'Conversational' },
  B2: { title: 'Intermediário alto', body: 'Voz passiva, phrasal verbs e ideias abstratas. Fluência à vista.', section: 'Fluent' },
  C1: { title: 'Avançado', body: 'Idioms, nuance e hedging — vamos polir seu inglês de filme.', section: 'Fluent' },
};

export function PlacementResult({
  outcome,
  onStart,
}: {
  outcome: PlacementOutcome;
  onStart: () => void;
}) {
  const copy = BAND_COPY[outcome.level];
  const bandRows = useMemo(
    () => BANDS.map((b) => ({ band: b, ...outcome.scorePerBand[b] })),
    [outcome.scorePerBand],
  );
  const bestIndex = BANDS.indexOf(outcome.level);

  return (
    <div className="sl-quiz">
      <header className="sl-quiz__topbar">
        <Logo size={28} /> <span>Slingu</span>
      </header>

      <main className="sl-quiz__main">
        <section className="sl-quiz__card">
          <span className="sl-quiz__eyebrow">
            <Icons.SparkleIcon size={14} />
            Resultado
          </span>
          <h1 className="sl-quiz__result-title">
            Seu nível:
            <span className="sl-quiz__result-level">
              {outcome.level} · {copy.title}
            </span>
          </h1>
          <p className="sl-quiz__intro-body">{copy.body}</p>

          <div className="sl-quiz__result-grid">
            {bandRows.map((r, i) => {
              const achieved = r.total > 0 && r.correct / r.total >= 0.7;
              const isCurrent = i === bestIndex;
              const pct = r.total === 0 ? 0 : (r.correct / r.total) * 100;
              return (
                <div
                  key={r.band}
                  className={`sl-quiz__band${achieved ? ' sl-quiz__band--achieved' : ''}${isCurrent ? ' sl-quiz__band--current' : ''}`}
                >
                  <span className="sl-quiz__band-tag">{r.band}</span>
                  <span className="sl-quiz__band-score">
                    {r.correct}/{r.total}
                  </span>
                  <div className="sl-quiz__band-bar">
                    <div className="sl-quiz__band-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          <p className="sl-quiz__label">
            Começaremos em{' '}
            <strong style={{ color: 'var(--sl-accent)' }}>{copy.section}</strong>.
            Dá pra trocar de unidade a qualquer momento na Home.
          </p>
        </section>
      </main>

      <footer className="sl-quiz__footer">
        <div className="sl-quiz__actions">
          <Button size="lg" onClick={onStart}>
            Começar agora
          </Button>
        </div>
      </footer>
    </div>
  );
}
